import { supabase } from '@/integrations/supabase/client';
import { Product } from '@/types/product';

/**
 * Lightweight product query optimized for browse page grid display
 * Fetches only essential fields to minimize payload size
 * Fetches products and user favorites in parallel for performance
 */
export async function getBrowseProducts(
  showUnpublished = false,
  userId?: string
): Promise<Product[]> {
  // Build products query
  let query = supabase
    .from('products')
    .select(`
      id,
      slug,
      title,
      subtitle,
      description,
      image,
      profile_image,
      price,
      msrp,
      price_duration,
      rating,
      review_count,
      categories,
      tags,
      service_type,
      is_published,
      is_hot,
      is_select,
      sort_order,
      created_at,
      best_for,
      integrations_text,
      skill_level,
      total_video_hours,
      certificate_included,
      student_count,
      access_type,
      instructor_info,
      course_duration,
      preview_lesson_url,
      circle_score,
      score_popularity,
      score_value,
      score_ease_of_use,
      score_support,
      score_roi,
      score_calculated_at,
      product_plans(price)
    `);
  
  // Only filter published when NOT in edit mode
  if (!showUnpublished) {
    query = query.eq('is_published', true);
  }
  
  query = query
    .order('sort_order', { ascending: true })
    .order('created_at', { ascending: false });

  // Execute products + favorites in PARALLEL for performance
  const [productsRes, favoritesRes] = await Promise.all([
    query,
    userId 
      ? supabase
          .from('user_favorites')
          .select('product_id')
          .eq('user_id', userId)
      : Promise.resolve({ data: null, error: null })
  ]);

  if (productsRes.error) throw productsRes.error;

  // Build favorites Set for O(1) lookup performance
  const favoriteSet = new Set(
    favoritesRes.data?.map(f => f.product_id) || []
  );

  // Transform to match expected format with integration_tags and isFavorited
  return (productsRes.data || []).map(product => {
    // Parse integration tags from integrations_text
    const integration_tags = product.integrations_text 
      ? product.integrations_text
          .split(/[,;|]/)
          .map((t: string) => t.trim())
          .filter((t: string) => t.length > 0)
      : [];
    
    // Build Circle Score object if score exists
    const circleScore = product.circle_score ? {
      circle_score: product.circle_score,
      score_popularity: product.score_popularity,
      score_value: product.score_value,
      score_ease_of_use: product.score_ease_of_use,
      score_support: product.score_support,
      score_roi: product.score_roi,
      score_calculated_at: product.score_calculated_at,
    } : null;

    return {
      ...product,
      integration_tags,
      isFavorited: favoriteSet.has(product.slug), // Attach favorite status
      circleScore,
      plansCount: product.product_plans?.length || 0,
      startingPrice: product.product_plans?.length 
        ? Math.min(...product.product_plans.map((p: any) => p.price || 0).filter((p: number) => p > 0))
        : product.price,
      // Provide defaults for fields not needed in browse grid
      gallery: [],
      features: [],
      categories: Array.isArray(product.categories) ? product.categories : [],
      tags: Array.isArray(product.tags) ? product.tags : [],
      best_for: Array.isArray(product.best_for) ? product.best_for : [],
    };
  }) as unknown as Product[];
}
